import type { CreatureArchetype } from "./creatures.ts";

/**
 * Camada social (doc 06: Social, Chat e comandos; doc 07: A11). Limites medidos no servidor;
 * hipóteses onde os documentos não fixam valores, centralizadas aqui.
 */
export const CHAT = {
  /** Até 140 caracteres por mensagem (o servidor corta; o campo do cliente também limita). */
  maxLength: 140,
  /** 1 mensagem por segundo, rajada de 4 (balde de fichas). */
  rate: 1,
  burst: 4,
  /** Linhas guardadas no cliente e quanto tempo uma linha nova fica à vista com o chat fechado. */
  history: 60,
  fade: 10,
} as const;

/** Tipos de ping (doc 06), inferidos pelo que a mira atinge. */
export const PING_KINDS = ["aqui", "perigo", "recurso", "item", "criatura"] as const;
export type PingKind = (typeof PING_KINDS)[number];

export const PING = {
  /** Cada ping dura 6 s; no máximo 3 ativos por jogador (o 4º substitui o mais velho). */
  seconds: 6,
  maxActive: 3,
  /** Ponto plausível: até 300 m de quem marca e à vista (o terreno não pode tapar a linha). */
  range: 300,
  /** 1 por segundo e no máximo 3 em 5 s. */
  gap: 1,
  burst: 3,
  window: 5,
  /** Criatura a até 5 m do ponto confirma "perigo"/"criatura"; sem ela, vira "aqui". */
  creatureReach: 5,
} as const;

/** Texto curto de cada ping (mundo, borda da tela e mapa) e o símbolo do tipo (a cor é de quem pingou, F75). */
export const PING_LABELS: Record<PingKind, { label: string; symbol: string }> = {
  aqui: { label: "Aqui", symbol: "●" },
  perigo: { label: "Perigo", symbol: "▲" },
  recurso: { label: "Recurso", symbol: "◆" },
  item: { label: "Item", symbol: "■" },
  criatura: { label: "Criatura", symbol: "◉" },
};

/** Fauna neutra é "criatura"; assombrações, chefes, Guardiões e o Boitatá são "perigo". */
export const pingKindForCreature = (archetype: CreatureArchetype): PingKind => (archetype === "neutra" ? "criatura" : "perigo");

export const MAP = {
  /** Zoom de 0,25 a 6× com arrasto (doc 06). */
  zoomMin: 0.25,
  zoomMax: 6,
  /** Névoa de exploração: revela 60 m em volta de cada aliado (compartilhada no Mutirão). */
  reveal: 60,
  /** Redemoinhos do Saci aparecem a menos de 60 m. */
  whirlwinds: 60,
} as const;

/** Votação de expulsão em jogo (doc 07, A11): maioria, só com 3 ou mais jogadores. */
export const KICK_VOTE = {
  minPlayers: 3,
  /** Dura 30 s; quem abriu espera 60 s para abrir outra. */
  seconds: 30,
  cooldown: 60,
} as const;

/** `/destravar` (doc 06): ponto seguro perto, fora de combate, recarga de 5 min. */
export const UNSTUCK = {
  cooldown: 300,
  /** Sem dar nem levar dano há 10 s. */
  combat: 10,
  /** Procura chão livre num raio de até 24 m, de 2 em 2 m. */
  search: 24,
  step: 2,
} as const;

/** Comandos do chat (doc 06), com os nomes curtos do roadmap como apelidos. */
export const CHAT_COMMANDS = [
  { name: "semente", aliases: ["seed"], usage: "/semente", help: "mostra e copia a seed da ilha" },
  { name: "latencia", aliases: ["ping"], usage: "/latencia", help: "mostra a latência medida pelo servidor" },
  { name: "ajuda", aliases: ["help"], usage: "/ajuda", help: "lista os comandos" },
  { name: "mandinga", aliases: ["pressagio", "presságio"], usage: "/presságio", help: "descreve o Presságio do Dia" },
  { name: "silenciar", aliases: [], usage: "/silenciar <apelido>", help: "silencia (ou volta a ouvir) alguém só para você" },
  { name: "expulsar", aliases: ["kick"], usage: "/expulsar <apelido>", help: "no lobby, só o dono; em jogo, abre uma votação" },
  { name: "votar", aliases: [], usage: "/votar sim|nao", help: "vota na expulsão em andamento" },
  { name: "destravar", aliases: [], usage: "/destravar", help: "leva a um ponto seguro perto (fora de combate, recarga de 5 min)" },
] as const;
export type ChatCommandName = (typeof CHAT_COMMANDS)[number]["name"];

/** Nome canônico de um comando digitado (com ou sem a barra, apelidos aceitos). */
export function chatCommand(word: string): ChatCommandName | undefined {
  const w = word.replace(/^\//, "").toLowerCase();
  return CHAT_COMMANDS.find((c) => c.name === w || (c.aliases as readonly string[]).includes(w))?.name;
}
